import { useMemo } from 'react';

import { Type as InputType } from '@/components/form/Input';
import { useAxios } from '@/hooks/useAxios';
import { AllIntegrations } from '@/hooks/useIntegration';
import { type IntegrationInput } from '@/types';
import { useQuery } from '@/utils/api';

interface BackendPluginStatus {
  plugin: string;
  name: string;
  hasMasterKey: boolean;
  description: string;
}

export interface PluginCredentialField {
  name: string;
  label: string;
  placeholder: string;
  required: boolean;
  isPassword: boolean;
}

export interface PluginStatus {
  plugin: string;
  name: string;
  hasMasterKey: boolean;
  description: string;
  credentialFields: PluginCredentialField[];
}

export function extractCredentialFields(
  inputs: IntegrationInput[] | undefined
): PluginCredentialField[] {
  if (!inputs) return [];
  return inputs
    .filter(
      input =>
        !input.hidden &&
        input.name !== 'api_usage_limit' &&
        input.name !== 'username' &&
        input.name !== 'value'
    )
    .map(input => ({
      name: input.name,
      label: typeof input.label === 'string' ? input.label : input.name,
      placeholder:
        typeof input.placeholder === 'string' ? input.placeholder : '',
      required: input.required ?? false,
      isPassword: input.type === InputType.PASSWORD,
    }));
}

const piusIntegrations = Object.values(AllIntegrations).filter(
  integration =>
    typeof integration.id === 'string' && integration.id.startsWith('pius_')
);

export function useEnrichmentPlugins() {
  const axios = useAxios({ doNotImpersonate: true });

  const backendQuery = useQuery<BackendPluginStatus[]>({
    defaultErrorMessage: 'Failed to fetch enrichment plugins',
    queryKey: ['enrichment', 'plugins'],
    queryFn: async () => {
      const { data } = await axios.get('/enrichment');
      return data;
    },
  });

  const data = useMemo(() => {
    const backendMap = new Map(
      (backendQuery.data ?? []).map(p => [p.plugin, p])
    );

    return piusIntegrations.map(integration => {
      const pluginId = integration.id as string;
      const backend = backendMap.get(pluginId);

      return {
        plugin: pluginId,
        name: integration.name,
        hasMasterKey: backend?.hasMasterKey ?? false,
        description:
          typeof integration.description === 'string'
            ? integration.description
            : (backend?.description ?? ''),
        credentialFields: extractCredentialFields(integration.inputs),
      } satisfies PluginStatus;
    });
  }, [backendQuery.data]);

  return {
    data,
    status: backendQuery.status,
  };
}
